import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import type { MessageVariant } from 'components/Message';
import { useRequiredContext } from 'hooks';

interface Toast {
	id: number;
	link?: string;
	linkText?: string;
	message: string;
	variant: MessageVariant;
}
type NewToast = Omit<Toast, 'id'>;

interface ToastContextType {
	closeToast: (id: number) => void;
	sendToast: (toast: NewToast) => void;
	toasts: Toast[] | undefined;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface Props {
	children: ReactNode;
}

export function ToastContextProvider({ children }: Props) {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const sendToast = useCallback((toast: NewToast) => {
		setToasts((currentToasts) => [
			...currentToasts,
			{ id: Math.random(), ...toast },
		]);
	}, []);

	const closeToast = useCallback((id: number) => {
		setToasts((currentToasts) =>
			currentToasts.filter((toast) => toast.id !== id),
		);
	}, []);

	const value = useMemo(
		() => ({ sendToast, toasts, closeToast }),
		[closeToast, sendToast, toasts],
	);

	return <ToastContext value={value}>{children}</ToastContext>;
}
ToastContextProvider.displayName = 'ToastContextProvider';

export function useToastContext() {
	return useRequiredContext(ToastContext, 'ToastContext');
}
